import { cartAdd, cartChange } from '@process-creative/slate-theme-tools';
import { SlideDrawer } from '../objects/slide-drawer';

import { bindAssignedElements } from './utils';
import './personalisation-page';
import './personalisation-navigation';
import './personalisation-navigation-buttons';
import './personalisation-font-selector';
import './personalisation-color-selector';
import './personalisation-image-selector';
import './personalisation-image';
import './christmas-personalisation-image';
import './personalisation-input';
import './personalisation-textarea';
import './personalisation-dropdown';

const template = document.createElement('template');
template.innerHTML = `
  <style>
    :host {
      height: 100%;
      width: 100%;
    }

    :host(.o-personalisation-engine) {
      font-family: 'BASEL CLASSIC', sans-serif;
    }

    .personalisation-engine {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
    }

    ::slotted(.o-personalisation-engine__column) {
      width: 100%;
      height: 100%;
      position: relative;
    }

    ::slotted(.o-personalisation-engine__column:first-of-type) {
      background-color: var(--gray);
      color: #FFFFFF;
      display: flex;
      align-items: center;
      flex-direction: column;
      overflow: hidden;
    }

    ::slotted(.o-personalisation-engine__column:not(.is-christmas):first-of-type) {
      justify-content: center;
    }

    ::slotted(.o-personalisation-engine__column:last-of-type) {
      display: flex;
      flex-direction: column;
      background-color: #FFFFFF;
      border-top-left-radius: 0.875rem;
      border-bottom-left-radius: 0.875rem;
      padding: 2rem 2.5rem 2.5rem; 
      text-align: center;
      max-width: 32.5rem;
      overflow-y: auto;
      width: 100%;
    } 

    ::slotted(.is-christmas.o-personalisation-engine__column:last-of-type) {
      padding: 2.43rem 2.5rem 2.5rem; 
    } 
      
    @media screen and (max-width: 1100px) {
      .personalisation-engine {
        flex-direction: column;
        overflow-y: auto;
        height: auto;
      }

      ::slotted(.is-christmas.o-personalisation-engine__column:last-of-type) {
        padding-left: 1.25rem;
        padding-right: 1.25rem;
        padding-bottom: 0;
      } 

      ::slotted(.o-personalisation-engine__column:first-of-type) {
        margin-bottom: 0;
        max-width: 100%;
        width: 100%;
      }

      ::slotted(.o-personalisation-engine__column:last-of-type) {
        border-radius: 0;
        padding: 0 1.25rem 0;
        max-width: 100%;
        width: 100%;
        overflow-y: auto;
      }
    }
  </style>

  <div class="personalisation-engine" part="root">
    <slot></slot>
  </div>
`;

type NavGuards = {
  [key:number]: () => boolean
}

type NavGuardParams = {
  index:number;
  condition:() => boolean;
  handler:(conditionResult:boolean) => void;
}

type PersonalisationData = {
  // These keys should match those that are set on all personalisation-input elements
  firstName:string;
  lastName:string;
  recipientName:string;
  senderName:string;
  message:string;
}

type PersonalisationColor = {
  fontColor: string;
  labelColor: string;
};

type PersonalisationTypeface = {
  fontType: string;
  fontValue: string;
}

type PersonalisationImageType = {
  imageType: string;
}

class PersonalisationEngine extends HTMLElement {
  private readonly root:HTMLDivElement|null;
  private currentPage:number = 0;
  private previousPage:number = 0;
  private navGuards:NavGuards = {};
  private isChristmasTemplate:boolean = false;
  private pages: any;
  private navigation: any;
  private navigationButtons: any;
  private fontSelector: any;
  private colorSelector: any;
  private imageSelector: any;
  private image: any;
  private input: any;
  private textarea: any;
  private dropdowns: any;
  private firstNameValue: string = "";
  
  private readonly personalisationData:PersonalisationData = {
    firstName: '',
    lastName: '',
    recipientName: '',
    senderName: '',
    message: '',
  }

  private readonly personalisationColor: PersonalisationColor = {
    fontColor: '',
    labelColor: ''
  };

  private readonly personalisationTypeface: PersonalisationTypeface = {
    fontType: '',
    fontValue: ''
  }

  private readonly personalisationImageType: PersonalisationImageType = {
    imageType: ''
  }

  constructor() {
    super();

    const shadowRoot = this.attachShadow({ mode: 'open' });
    shadowRoot.appendChild(template.content.cloneNode(true));

    this.root = shadowRoot.querySelector('.personalisation-engine')!;

    const slotElement = shadowRoot.querySelector('slot');
    const getSubComponent = bindAssignedElements(slotElement);

    this.pages = getSubComponent('personalisation-page');
    this.navigation = getSubComponent('personalisation-navigation');
    this.navigationButtons = getSubComponent('personalisation-navigation-buttons');
    this.fontSelector = getSubComponent('personalisation-font-selector');
    this.colorSelector = getSubComponent('personalisation-color-selector');
    this.imageSelector = getSubComponent('personalisation-image-selector');
    this.image = getSubComponent('personalisation-image') || getSubComponent('christmas-personalisation-image');
    this.input = getSubComponent('personalisation-input');
    this.textarea = getSubComponent('personalisation-textarea');
    this.dropdowns = getSubComponent('personalisation-dropdown');
    this.isChristmasTemplate = this.classList.contains('is-christmas');
    this.firstNameValue = this.querySelector('[data-fname-value]')?.getAttribute('data-fname-value') || "";
    
    
    this.setNavGuard({
      index: 0, // This is the step index to prevent navigation on
      condition: this.isChristmasTemplate
        ? () => !!this.personalisationData.recipientName.length && !!this.personalisationData.senderName.length // Christmas condition
        : () => !!this.personalisationData.firstName.length || !!this.firstNameValue?.length, // Non-Christmas condition
      handler: (result) => { // this code will run after the condition is evaluated. The result of the condition function is passed through

        // set the state of the nav buttons in the nav menu and bottom of right column

  
      }
    });

    // this event fired from navigation
    this.addEventListener('navigation', this.handleNavigation);
    this.addEventListener('data_update', this.handleUpdateInfo);
    this.addEventListener('dropdown_toggle', this.handleDropdownToggle);
    this.addEventListener('color_selected', this.handleColorSelected);
    this.addEventListener('font_selected', this.handleFontSelected);
    this.addEventListener('image_selected', this.handleImageSelected);
    this.addEventListener('add_to_cart', this.addPersonaliseCart);
    this.addEventListener('edit_cart', this.editPersonaliseCart);

    this.setUiForCurrentPage();
  }

  public initializeComponents() {
    // Re-query all child components to ensure they're properly initialized
    const slotElement = this.shadowRoot?.querySelector('slot');
    if (!slotElement) {
      console.error('❌ Slot element not found in shadow root');
      return;
    }
    
    const getSubComponent = bindAssignedElements(slotElement);
    
    this.pages = getSubComponent('personalisation-page');
    this.navigation = getSubComponent('personalisation-navigation');
    this.navigationButtons = getSubComponent('personalisation-navigation-buttons');
    this.fontSelector = getSubComponent('personalisation-font-selector');
    this.colorSelector = getSubComponent('personalisation-color-selector');
    this.imageSelector = getSubComponent('personalisation-image-selector');
    this.image = getSubComponent('personalisation-image') || getSubComponent('christmas-personalisation-image');
    this.input = getSubComponent('personalisation-input');
    this.textarea = getSubComponent('personalisation-textarea');
    this.dropdowns = getSubComponent('personalisation-dropdown');
    this.firstNameValue = this.querySelector('[data-fname-value]')?.getAttribute('data-fname-value') || "";
    
    // Re-setup navigation guard
    this.setNavGuard({
      index: 0,
      condition: this.isChristmasTemplate
        ? () => !!this.personalisationData.recipientName.length && !!this.personalisationData.senderName.length
        : () => !!this.personalisationData.firstName.length || !!this.firstNameValue?.length,
      handler: (result) => {
        // set the state of the nav buttons in the nav menu and bottom of right column
      }
    });
    
    // Load existing data from attributes if editing
    this.loadExistingData();
    
    // Re-initialize UI
    this.setUiForCurrentPage();
  }

  private loadExistingData() {
    // Load existing personalisation data from data attributes if available
    const fnameValue = this.querySelector('[data-fname-value]')?.getAttribute('data-fname-value') || "";
    const lnameValue = this.querySelector('[data-lname-value]')?.getAttribute('data-lname-value') || "";
    const messageValue = this.querySelector('[data-message-value]')?.getAttribute('data-message-value') || "";
    
    if (fnameValue) {
      this.personalisationData.firstName = fnameValue;
    }
    if (lnameValue) {
      this.personalisationData.lastName = lnameValue;
    }
    if (messageValue) {
      this.personalisationData.message = messageValue;
    }
    
    // Update the label with existing data
    if (fnameValue || lnameValue || messageValue) {
      setTimeout(() => {
        this.setLabel();
      }, 100);
    }
  }

  get lastPageIndex():number {
    return this.pages.length - 1;
  }

  setNavGuard(params:NavGuardParams) {
    const { index, condition, handler } = params;
  
    this.navGuards[index] = () => {
      const navigationIsAllowed = condition();
      if (handler) handler(navigationIsAllowed);
      return navigationIsAllowed;
    }
  }

  navigationIsAllowed(navToPage:number) {
    const navGuardForStep = this.navGuards[navToPage];

    if (!navGuardForStep) return true; // allow navigation

    const conditionResult = navGuardForStep(); // Run the function to get the updated condition and run handlers

    if(navToPage < this.currentPage) {
      return true; // We're always allowed to go back
    }

    return conditionResult;
  }

  handleUpdateInfo(e) {
    const data = e.detail.data;

    // Loop through each key-value pair in the data
    Object.keys(data).forEach(key => {
      const value = data[key];
      this.personalisationData[key] = value;
  
      // Check specific cases and adjust font size if needed
      if (key === "firstName" || key === "lastName") {
        this.image.bottleName?.forEach(element => {
          this.image.adjustFontSize(element);
        });
      }
  
      if (key === "message") {
        this.image.bottleMessage?.forEach(element => {
          this.image.adjustFontSize(element, 2);
        });
      }
    });
  
    this.setLabel();
    this.navigationIsAllowed(this.currentPage + 1);
  }

  private handleColorSelected(e: Event) {
    const { colorName, colorValue, type } = e.detail;

    this.personalisationColor[type] = `${colorName}:${colorValue}`;
    const colorSelector: PersonalisationColorSelector | undefined = Array.from(this.colorSelector).find(selector => selector.componentKey === type);
    colorSelector?.setActiveColor(colorName);
    this.setLabel();
  }

  handleFontSelected(e) {
    const { fontType, fontValue } = e.detail;
    this.personalisationTypeface.fontValue = fontValue;
    this.personalisationTypeface.fontType = fontType;
    this.setLabel();
    this.updateBottleHeading(fontType);
  }

  handleImageSelected(e) {
    const { imageType } = e.detail;
    this.personalisationImageType.imageType = imageType;
    this.setImage();
  }

  setLabel() {
    this.image.setLabel(this.personalisationTypeface.fontType,this.personalisationData,this.personalisationColor)
  }
  
  setImage() {
    this.image.setImage(this.personalisationImageType.imageType);
  }

  updateBottleHeading(fontType: string) {
    this.image.updateBottleHeading(fontType);
  }

  public async addPersonaliseCart() {
    if (!this.navigationIsAllowed(this.currentPage)) {
      console.warn(`Not allowed to add to cart. Check the navGuards for this page.`);
      return;
    }
    
    const product_id = Number(this.getAttribute('data-product-id'));
    const variant_id = Number(this.getAttribute('data-variant-id'));

    let fontColorName = this.personalisationColor.fontColor.split(":")
    let labelColorName = this.personalisationColor.labelColor.split(":")
    let properties = {};

    if (this.personalisationData.recipientName) {
      properties = {
        recipientName: `${this.personalisationData.recipientName}`,
        senderName: `${this.personalisationData.senderName}`
      };
    } else {
      properties = {
        isPersonalised: true,
        firstName: this.personalisationData.firstName.toUpperCase(),
        lastName: this.personalisationData.lastName.toUpperCase(),
        message: this.personalisationData.message ? this.personalisationData.message.toUpperCase() : null,
        fontColor: fontColorName[0],
        labelColor: labelColorName[0],
        fontColorCode: fontColorName[1],
        labelColorCode: labelColorName[1],
        fontType: this.personalisationTypeface.fontType,
        imageType: this.personalisationImageType.imageType,
      };
    }
    
    const data = {
      id: variant_id,
      "product-id": product_id,
      form_type: "product",
      quantity: 1,
      properties
    };
  
    await cartAdd({ items: [data] });

    const customEvent = new CustomEvent('trigger_open', {
      detail: false, 
      bubbles: true,
      composed: true
    })

    this.dispatchEvent(customEvent);

    this.clearForm()
  }

  public async editPersonaliseCart() {
     if (!this.navigationIsAllowed(this.currentPage)) {
      console.warn(`Not allowed to add to cart. Check the navGuards for this page.`);
      return;
    }

    const cartItemKey = this.querySelector('[data-cart-edit]')?.getAttribute('data-cart-edit') || '';

    let fontColorName = this.personalisationColor.fontColor.split(":")
    let labelColorName = this.personalisationColor.labelColor.split(":")
    let properties = {};

    if (this.personalisationData.recipientName) {
      properties = {
        recipientName: `${this.personalisationData.recipientName}`,
        senderName: `${this.personalisationData.senderName}`
      };
    } else {
      properties = {
        isPersonalised: true,
        firstName: this.personalisationData.firstName.toUpperCase(),
        lastName: this.personalisationData.lastName.toUpperCase(),
        message: this.personalisationData.message ? this.personalisationData.message.toUpperCase() : null,
        fontColor: fontColorName[0],
        labelColor: labelColorName[0],
        fontColorCode: fontColorName[1],
        labelColorCode: labelColorName[1],
        fontType: this.personalisationTypeface.fontType,
        imageType: this.personalisationImageType.imageType,
      };
    }

    await cartChange({
      id: cartItemKey,
      properties: properties,
    });

    const customEvent = new CustomEvent('trigger_open', {
      detail: false, 
      bubbles: true,
      composed: true
    })

    this.dispatchEvent(customEvent);
    const drawer = document.querySelector<SlideDrawer>('[data-personalisation-edit-engine]');
    if (drawer) {
      drawer.visible = false;
    }

    this.clearForm()
  }

  private clearForm() {
    this.input.forEach(inputComponent => {
      inputComponent.clearInput();
    });

    this.textarea?.clearInput();
    
    setTimeout(() => {
      this.image.clearName();
      if (!this.isChristmasTemplate) this.image.clearMessage();

      const event = new CustomEvent('data_update', { 
        detail: { 
          data: { 
            firstName: '', 
            lastName: '', 
            message: '',
            senderName: '', 
            recipientName: '' 
          }
        },
        bubbles: true, 
        composed: true 
      });
  
      this.dispatchEvent(event);
    }, 500);

    this.fontSelector?.updateActiveLabel('traditional');
    this.fontSelector?.setDefaultFont();

    this.imageSelector?.updateActiveLabel('bottle');
    this.imageSelector?.setDefaultImageType();

    this.currentPage = 0;
    this.setUiForCurrentPage();
  }
  

  private showPage() {
    if (!this.pages) {
      console.warn('⚠️ No pages found to show');
      return;
    }

    if (this.pages instanceof HTMLElement) {
      this.pages = [this.pages];
    } else if (this.pages instanceof NodeList) {
      this.pages = Array.from(this.pages);
    }
    
    this.pages.forEach((step, index) => {
      // Check if step has setActive method
      if (step && typeof step.setActive === 'function') {
        step.setActive(index === this.currentPage);
      } else {
        console.error('❌ Page element does not have setActive method:', step);
      }
    });
  }

  private setUiForCurrentPage() {
    this.showPage();

    // Handle navigation - ensure we're calling on a single element
    if (this.navigation) {
      if (this.navigation instanceof NodeList || Array.isArray(this.navigation)) {
        const navElement = this.navigation[0];
        if (navElement && typeof navElement.setActive === 'function') {
          navElement.setActive(this.currentPage);
        }
      } else if (typeof this.navigation.setActive === 'function') {
        this.navigation.setActive(this.currentPage);
      }
    }

    // Handle navigation buttons - ensure we're calling on a single element
    if (this.navigationButtons) {
      if (this.navigationButtons instanceof NodeList || Array.isArray(this.navigationButtons)) {
        const navButtonsElement = this.navigationButtons[0];
        if (navButtonsElement && typeof navButtonsElement.setActive === 'function') {
          navButtonsElement.setActive(this.currentPage, this.lastPageIndex);
        }
      } else if (typeof this.navigationButtons.setActive === 'function') {
        this.navigationButtons.setActive(this.currentPage, this.lastPageIndex);
      }
    }

  }

  private handleDropdownToggle(e) {
    const { detail: dropdown } = e;
    dropdown.setActive(!dropdown.isActive);

    if (!dropdown.isActive) {
      const inputElements = dropdown.querySelectorAll('personalisation-input');

      inputElements.forEach(inputElement => {
        if (inputElement) {
          inputElement.clearInput();
          this.image.clearMessage()
        }
      });
    }
  }

  private handleNavigation(e) {
    const { pageIndex, fromButton } = e.detail;

    const targetPage = fromButton ? this.currentPage + pageIndex : pageIndex;

    // Check if allowed to navigate away from current Page 
    if (!this.navigationIsAllowed(this.currentPage)) {
      console.warn(`Not allowed to navigate away from pageIndex ${this.currentPage}. Check the navGuards for this page.`);
      return;
    }
  
    this.previousPage = this.currentPage;
    this.currentPage = targetPage;
    this.setUiForCurrentPage();
  }
}

if(!customElements.get('personalisation-engine')) {
  customElements.define('personalisation-engine', PersonalisationEngine);
}